import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor,
  useSensor, useSensors, useDraggable, useDroppable,
} from '@dnd-kit/core';
import { depenses, notes } from '../services/api';
import { televerserJustificatif } from '../services/upload';
import { eur, km, dateFr } from '../services/format';
import { useNdf } from '../NdfContext';
import { IcCamera, IcGalerie, IcFichier, IcVoiture, IcEclair } from './Icones';
import CameraRafale, { cameraDisponible } from './CameraRafale';
import './MesDepenses.css';

function titreDepense(d) {
  return d.type === 'km'
    ? `${km(d.distance_km)} — ${d.motif_deplacement || 'déplacement'}`
    : (d.marchand || 'Justificatif à vérifier');
}

function statutLigne(d) {
  if (d.statut_analyse === 'analyse') return <span className="md-analyse"><span className="md-spinner" /> Analyse…</span>;
  if (d.statut_ligne === 'refusee') return <span className="nf-statut nf-statut--refusee" title={d.motif_refus || ''}>Écartée</span>;
  if (d.statut_analyse === 'erreur') return <span className="nf-alerte">Lecture impossible</span>;
  if (!d.date_depense || d.montant_ttc === null || !d.categorie_id) return <span className="nf-alerte">À compléter</span>;
  // Une lecture IA n'est « Prête » que si TOUT est bon (TVA comprise) ; une
  // correction humaine fait foi.
  if (d.justificatif_key && !d.edite_manuellement && d.confiance !== 'haute') {
    return <span className="nf-alerte">À vérifier</span>;
  }
  return <span className="nf-statut nf-statut--validee">Prête</span>;
}

// Une ligne de dépense, glissable vers un dossier (souris : glisser ;
// mobile : appui long puis glisser — un simple appui ouvre la fiche).
function LigneDepense({ d }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `dep-${d.id}`,
    data: { depense: d },
  });
  return (
    <li ref={setNodeRef} {...listeners} {...attributes} className={isDragging ? 'md-ligne-fantome' : ''}>
      <Link to={`/depense/${d.id}`} className="md-ligne" draggable={false}>
        <span className="md-ligne-icone" aria-hidden="true">{d.categorie_icone || '🧾'}</span>
        <span className="md-ligne-corps">
          <span className="md-ligne-titre">{titreDepense(d)}</span>
          <span className="md-ligne-sous">
            {d.date_depense ? dateFr(d.date_depense) : 'date ?'}
            {d.categorie_libelle ? ` · ${d.categorie_libelle}` : ''}
          </span>
          {d.statut_ligne === 'refusee' && d.motif_refus && (
            <span className="md-ligne-motif">Motif : {d.motif_refus}</span>
          )}
        </span>
        <span className="md-ligne-droite">
          <span className="nf-montant">{eur(d.montant_ttc)}</span>
          {statutLigne(d)}
        </span>
      </Link>
    </li>
  );
}

// La carte d'un dossier : repliée par défaut, cible de dépôt pendant un glisser.
function CarteDossier({ sec, deplie, glisserActif, onToggle, onSoumettre, onAjouter, soumissionBloquee }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `dos-${sec.dossier_id}`,
    data: { dossier_id: sec.dossier_id },
  });
  const sousTotal = sec.depenses.reduce((s, d) => s + (parseFloat(d.montant_ttc) || 0), 0);
  const analyseSec = sec.depenses.some((d) => d.statut_analyse === 'analyse');
  const alerteSec = sec.depenses.some((d) => d.statut_ligne === 'refusee' || d.statut_analyse === 'erreur'
    || !d.date_depense || d.montant_ttc === null || !d.categorie_id);
  return (
    <section className="md-dossier">
      <div ref={setNodeRef}
        className={`md-dossier-carte ${isOver ? 'md-survol' : ''} ${glisserActif ? 'md-cible' : ''}`}>
        <div className="md-dossier-rangee">
          <div className="md-dossier-l1">
            <button type="button" className="md-dossier-toggle" onClick={onToggle} aria-expanded={deplie}>
              <span className="md-dossier-titre">{sec.libelle}</span>
            </button>
            <span className="nf-montant md-dossier-total">{eur(sousTotal)}</span>
          </div>
          <div className="md-dossier-l2">
            <button type="button" className="md-dossier-contenu" onClick={onToggle} aria-expanded={deplie}>
              <span className={`md-chevron ${deplie ? 'md-chevron--ouvert' : ''}`} aria-hidden="true">▸</span>
              <span className="md-dossier-sous">
                {sec.depenses.length} dépense{sec.depenses.length > 1 ? 's' : ''}
                {analyseSec ? <span className="md-dossier-etat"> · analyse en cours</span>
                  : alerteSec ? <span className="md-dossier-etat md-dossier-etat--alerte"> · à vérifier</span>
                  : <span className="md-dossier-etat md-dossier-etat--ok"> · prêtes</span>}
              </span>
            </button>
            <span className="md-dossier-actions">
              <button type="button" className="nf-btn md-ajout-dossier" onClick={onAjouter}
                aria-label={`Ajouter des justificatifs au dossier ${sec.libelle}`}
                title="Ajouter des justificatifs (rafale)">
                <IcCamera taille={16} />
              </button>
              <button type="button" className="nf-btn md-soumettre-dossier"
                onClick={onSoumettre} disabled={soumissionBloquee || analyseSec}>
                Soumettre
              </button>
            </span>
          </div>
        </div>
      </div>
      {deplie && (
        <ul className="md-liste md-liste--dossier">
          {sec.depenses.map((d) => <LigneDepense key={d.id} d={d} />)}
        </ul>
      )}
    </section>
  );
}

// La zone « non classées » est aussi une cible : on peut ressortir une
// dépense d'un dossier en la déposant ici.
function ZoneNonClassees({ depensesLibres, aDesDossiers, glisserActif }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'dos-null', data: { dossier_id: null } });
  if (!depensesLibres.length && !(aDesDossiers && glisserActif)) return null;
  return (
    <div ref={setNodeRef} className={`md-zone-libre ${isOver ? 'md-survol' : ''} ${glisserActif ? 'md-cible' : ''}`}>
      {aDesDossiers && <h3 className="md-sous-titre">Dépenses non classées</h3>}
      <ul className="md-liste">
        {depensesLibres.map((d) => <LigneDepense key={d.id} d={d} />)}
      </ul>
      {!depensesLibres.length && <p className="md-zone-libre-vide">Déposez ici pour retirer d'un dossier</p>}
    </div>
  );
}

// L'écran d'accueil, pensé téléphone d'abord : photographier un ticket doit
// prendre trois secondes. Les dossiers sont repliés (le détail au déplié) ;
// une dépense se classe en la glissant sur un dossier.
function MesDepenses() {
  const { dossiers } = useNdf();
  const [liste, setListe] = useState([]);
  const [envois, setEnvois] = useState([]);
  const [erreur, setErreur] = useState('');
  const [soumission, setSoumission] = useState(false);
  const [deplies, setDeplies] = useState(() => new Set());
  const [glissee, setGlissee] = useState(null); // dépense en cours de glisser
  const [destination, setDestination] = useState(null); // dossier cible des captures
  const [rafaleOuverte, setRafaleOuverte] = useState(false);
  const [glisserFichier, setGlisserFichier] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { dossierId, libelle, nb, total }
  const [succes, setSucces] = useState(null); // { note_id } après soumission
  const navigate = useNavigate();
  const inputCamera = useRef(null);
  const inputGalerie = useRef(null);
  const inputFichier = useRef(null);
  const inputRafale = useRef(null);

  // Souris : le glisser démarre après 8 px (le clic simple reste un clic).
  // Tactile : appui long 220 ms (le défilement et l'appui simple restent naturels).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } })
  );

  const charger = useCallback(() => {
    depenses.brouillon()
      .then((res) => setListe(res.data.depenses))
      .catch(() => {});
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const analyseEnCours = liste.some((d) => d.statut_analyse === 'analyse');
  useEffect(() => {
    if (!analyseEnCours) return undefined;
    const t = setInterval(charger, 2500);
    return () => clearInterval(t);
  }, [analyseEnCours, charger]);

  const traiterFichiers = async (files, ouvrirSiSeul = true) => {
    setErreur('');
    const fichiers = Array.from(files || []);
    if (!fichiers.length) return;
    const seul = fichiers.length === 1 && ouvrirSiSeul;
    for (const f of fichiers) {
      const nom = `${f.name}-${Date.now()}`;
      setEnvois((e) => [...e, nom]);
      try {
        const id = await televerserJustificatif(f, destination);
        setEnvois((e) => e.filter((x) => x !== nom));
        if (seul) {
          navigate(`/depense/${id}`);
          return;
        }
        charger();
      } catch (err) {
        setEnvois((e) => e.filter((x) => x !== nom));
        setErreur(err.response?.data?.error || err.message || "Échec de l'envoi");
      }
    }
  };

  const surSelection = () => (e) => {
    traiterFichiers(e.target.files);
    e.target.value = '';
  };

  // Rafale : caméra intégrée (l'aperçu reste ouvert entre deux photos — seule
  // façon d'enchaîner sur iOS). Hors HTTPS, repli sur l'appareil natif :
  // une photo à la fois.
  const surRafale = (e) => {
    const fichiers = Array.from(e.target.files || []);
    e.target.value = '';
    if (!fichiers.length) return;
    traiterFichiers(fichiers, false);
  };

  const ouvrirRafale = () => {
    if (cameraDisponible()) setRafaleOuverte(true);
    else inputRafale.current.click();
  };

  const rafaleVers = (dossierId) => {
    setDestination(dossierId);
    if (cameraDisponible()) setRafaleOuverte(true);
    else inputRafale.current.click();
  };

  const surPhotoRafale = (file) => {
    traiterFichiers([file], false);
  };

  const soumettre = async (dossierId) => {
    setConfirmation(null);
    setErreur('');
    setSucces(null);
    setSoumission(true);
    try {
      const res = await notes.soumettre(dossierId);
      // On reste sur l'accueil : la liste se rafraîchit, un bandeau confirme.
      setSucces({ note_id: res.data.note_id });
      charger();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Soumission impossible');
    } finally {
      setSoumission(false);
    }
  };

  // Le bandeau de succès s'efface tout seul.
  useEffect(() => {
    if (!succes) return undefined;
    const t = setTimeout(() => setSucces(null), 8000);
    return () => clearTimeout(t);
  }, [succes]);

  const basculerDossier = (id) => setDeplies((prev) => {
    const suivant = new Set(prev);
    if (suivant.has(id)) suivant.delete(id); else suivant.add(id);
    return suivant;
  });

  // Dépôt : reclassement optimiste, puis le serveur confirme.
  const surFinGlisser = async (event) => {
    const dep = glissee;
    setGlissee(null);
    if (!dep || !event.over) return;
    const cible = event.over.data.current.dossier_id;
    if ((dep.dossier_id ?? null) === cible) return;
    setListe((l) => l.map((d) => d.id === dep.id ? { ...d, dossier_id: cible } : d));
    try {
      await depenses.modifier(dep.id, { dossier_id: cible });
      charger();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Reclassement impossible');
      charger();
    }
  };

  const total = liste.reduce((s, d) => s + (parseFloat(d.montant_ttc) || 0), 0);
  const pretes = liste.filter((d) => d.statut_analyse !== 'analyse').length;

  // Groupement par dossier : les libellés portés par les dépenses.
  const sections = [];
  for (const d of liste) {
    if (d.dossier_id === null) continue;
    let sec = sections.find((x) => x.dossier_id === d.dossier_id);
    if (!sec) {
      sec = { dossier_id: d.dossier_id, libelle: d.dossier_libelle, depenses: [] };
      sections.push(sec);
    }
    sec.depenses.push(d);
  }
  const libres = liste.filter((d) => d.dossier_id === null);

  const surDragOver = (e) => {
    if (e.dataTransfer && [...e.dataTransfer.types].includes('Files')) {
      e.preventDefault();
      setGlisserFichier(true);
    }
  };
  const surDrop = (e) => {
    e.preventDefault();
    setGlisserFichier(false);
    const fichiers = [...(e.dataTransfer?.files || [])]
      .filter((f) => /^image\//.test(f.type) || f.type === 'application/pdf');
    if (fichiers.length) traiterFichiers(fichiers, fichiers.length === 1);
  };

  return (
    <div className="md-page" onDragOver={surDragOver}
      onDragLeave={(e) => { if (e.target === e.currentTarget) setGlisserFichier(false); }}
      onDrop={surDrop}>
      <input ref={inputCamera} type="file" accept="image/*" capture="environment" hidden onChange={surSelection()} />
      <input ref={inputGalerie} type="file" accept="image/*" multiple hidden onChange={surSelection()} />
      <input ref={inputFichier} type="file" accept="image/*,application/pdf" multiple hidden onChange={surSelection()} />
      <input ref={inputRafale} type="file" accept="image/*" capture="environment" hidden onChange={surRafale} />

      <div className="md-ajout">
        <div className="md-zone-rose">
          <button type="button" className="md-scanner" onClick={() => inputCamera.current.click()}>
            <IcCamera taille={30} />
            <span className="md-scanner-txt">
              <span>Scanner un justificatif</span>
              <span className="md-scanner-sous">photographiez, le reste est automatique</span>
            </span>
          </button>
          <button type="button" className="md-rafale" onClick={ouvrirRafale}>
            <IcEclair taille={20} />
            <span>Rafale</span>
          </button>
        </div>
        <button type="button" className={`md-depot ${glisserFichier ? 'md-depot--survol' : ''}`}
          onClick={() => inputFichier.current.click()}>
          <IcFichier taille={26} />
          <span className="md-depot-titre">Glissez vos justificatifs ici</span>
          <span className="md-depot-sous">ou cliquez pour parcourir — images et PDF, plusieurs à la fois</span>
        </button>
        <div className="md-ajout-actions">
          <button type="button" className="md-ajout-action md-action-mobile" onClick={() => inputGalerie.current.click()}><IcGalerie taille={18} /> Galerie</button>
          <button type="button" className="md-ajout-action md-action-mobile" onClick={() => inputFichier.current.click()}><IcFichier taille={18} /> PDF</button>
          <Link to="/km" className="md-ajout-action"><IcVoiture taille={18} /> Kilomètres</Link>
        </div>
      </div>

      {dossiers.some((dd) => dd.statut === 'ouvert') && (
        <div className="md-destination">
          <span className="md-destination-titre">Classer dans</span>
          <button type="button" className={`md-dest-chip ${destination === null ? 'active' : ''}`}
            onClick={() => setDestination(null)}>Aucun</button>
          {dossiers.filter((dd) => dd.statut === 'ouvert').map((dd) => (
            <button key={dd.id} type="button" className={`md-dest-chip ${destination === dd.id ? 'active' : ''}`}
              onClick={() => setDestination(dd.id)}>📁 {dd.libelle}</button>
          ))}
        </div>
      )}

      {erreur && <div className="nf-erreur-bandeau">{erreur}</div>}
      {succes && (
        <div className="md-succes" role="status">
          ✓ Note soumise à votre valideur
          <Link to={`/note/${succes.note_id}`} className="md-succes-lien">voir</Link>
        </div>
      )}

      {envois.map((nom) => (
        <div key={nom} className="md-envoi"><span className="md-spinner" /> Envoi de {nom}…</div>
      ))}

      <div className="md-liste-entete">
        <h2 className="nf-titre-page">Dépenses à soumettre</h2>
        {liste.length > 0 && (
          <span className="md-total">
            {liste.length} dépense{liste.length > 1 ? 's' : ''} · <span className="nf-montant">{eur(total)}</span>
          </span>
        )}
      </div>

      {liste.length === 0 && !envois.length ? (
        <div className="nf-vide">
          <h2>Aucune dépense en attente</h2>
          <p>Photographiez un ticket : il sera lu, chiffré et classé automatiquement.</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={(e) => setGlissee(e.active.data.current.depense)}
          onDragCancel={() => setGlissee(null)}
          onDragEnd={surFinGlisser}
        >
          {sections.map((sec) => (
            <CarteDossier
              key={sec.dossier_id}
              sec={sec}
              deplie={deplies.has(sec.dossier_id)}
              glisserActif={!!glissee && (glissee.dossier_id ?? null) !== sec.dossier_id}
              onToggle={() => basculerDossier(sec.dossier_id)}
              onAjouter={() => rafaleVers(sec.dossier_id)}
              onSoumettre={() => setConfirmation({
                dossierId: sec.dossier_id,
                libelle: sec.libelle,
                nb: sec.depenses.length,
                total: sec.depenses.reduce((s, d) => s + (parseFloat(d.montant_ttc) || 0), 0),
              })}
              soumissionBloquee={soumission}
            />
          ))}

          <ZoneNonClassees
            depensesLibres={libres}
            aDesDossiers={sections.length > 0}
            glisserActif={!!glissee && glissee.dossier_id !== null}
          />

          <DragOverlay dropAnimation={null}>
            {glissee && (
              <div className="md-glisser-pilule">
                {titreDepense(glissee)} · {eur(glissee.montant_ttc)}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {liste.length > 0 && (
        <div className="md-soumettre-zone">
          <button
            type="button"
            className="nf-btn nf-btn--principal md-soumettre"
            onClick={() => setConfirmation({ dossierId: null, libelle: null, nb: liste.length, total })}
            disabled={soumission || analyseEnCours}
          >
            {analyseEnCours
              ? 'Analyses en cours…'
              : `${sections.length > 0 ? 'Tout soumettre' : 'Soumettre ma note'} (${pretes} dépense${pretes > 1 ? 's' : ''} · ${eur(total)})`}
          </button>
          <p className="md-soumettre-aide">La note part chez votre valideur, puis à la comptabilité.</p>
        </div>
      )}

      {rafaleOuverte && (
        <CameraRafale
          onCapture={surPhotoRafale}
          onClose={() => setRafaleOuverte(false)}
          enEnvoi={envois.length}
        />
      )}

      {confirmation && (
        <div className="md-confirm-fond" onClick={() => setConfirmation(null)} role="dialog" aria-modal="true">
          <div className="md-confirm" onClick={(e) => e.stopPropagation()}>
            <h3 className="md-confirm-titre">Soumettre la note ?</h3>
            <p className="md-confirm-detail">
              {confirmation.nb} dépense{confirmation.nb > 1 ? 's' : ''} · <strong className="nf-montant">{eur(confirmation.total)}</strong>
              {confirmation.libelle ? <><br />📁 {confirmation.libelle}</> : null}
            </p>
            <p className="md-confirm-aide">La note partira chez votre valideur — elle ne sera plus modifiable.</p>
            <div className="md-confirm-actions">
              <button type="button" className="nf-btn" onClick={() => setConfirmation(null)}>Annuler</button>
              <button type="button" className="nf-btn nf-btn--principal" onClick={() => soumettre(confirmation.dossierId)}>
                Soumettre
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MesDepenses;
